import { ExternalLink, Sparkles } from "lucide-react";
import * as React from "react";
import { Markdown } from "src/components/ui/Markdown";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "src/components/ui/sheet";

import type { CustomSkillSummaryType } from "./useCustomSkills";

interface SkillSheetProps {
  skill: CustomSkillSummaryType;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

function buildGithubUrl(repo: string | null | undefined, path: string | null | undefined): string | null {
  if (!repo) return null;
  const base = `https://github.com/${repo}`;
  if (!path) return base;
  return `${base}/tree/main/${path.replace(/^\/+/, "")}`;
}

export const SkillSheet: React.FC<SkillSheetProps> = ({ skill, isOpen, onOpenChange }) => {
  const githubUrl = buildGithubUrl(skill.githubRepoFullName, skill.githubDirectoryPath);
  const isGithubSourced = skill.sourceType === "CODE_GITHUB";

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto custom-scrollbar">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-400 flex-shrink-0" />
            <span className="text-muted-foreground font-normal">Skill</span>
            <span>{skill.name}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <span className="bg-card border border-border rounded px-2 py-0.5 text-muted-foreground">
            {isGithubSourced ? "GitHub-sourced" : "Managed"}
          </span>
          {skill.integration?.name && (
            <span className="bg-card border border-border rounded px-2 py-0.5 text-muted-foreground">
              Integration: {skill.integration.name}
            </span>
          )}
        </div>

        {skill.description && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Description</div>
            <div className="text-sm text-foreground">
              <Markdown>{skill.description}</Markdown>
            </div>
          </div>
        )}

        {isGithubSourced && (skill.githubRepoFullName || githubUrl) && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Source</div>
            <div className="flex flex-col gap-1 text-sm">
              {skill.githubRepoFullName && (
                <div className="text-foreground">
                  <span className="text-muted-foreground">Repository:</span>{" "}
                  <code className="bg-card border border-border rounded px-1.5 py-0.5 text-xs">
                    {skill.githubRepoFullName}
                  </code>
                </div>
              )}
              {skill.githubDirectoryPath && (
                <div className="text-foreground">
                  <span className="text-muted-foreground">Directory:</span>{" "}
                  <code className="bg-card border border-border rounded px-1.5 py-0.5 text-xs">
                    {skill.githubDirectoryPath}
                  </code>
                </div>
              )}
              {githubUrl && (
                <a
                  href={githubUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline mt-1 w-fit"
                >
                  View on GitHub
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        )}

        {skill.body && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">SKILL.md</div>
            <div className="text-sm text-foreground border border-border rounded p-3 bg-card">
              <Markdown>{skill.body}</Markdown>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
